import type { VisualComponent } from '@dtwin/shared';

export function getChartData(component: VisualComponent): unknown {
  if (!component.dataSource) return [];
  if (component.dataSource.type === 'static') {
    const config = component.dataSource.config;
    if (typeof config === 'object' && config !== null && 'data' in config) {
      return (config as { data: unknown }).data;
    }
  }
  return [];
}

/** 合并读取控件配置：base > special > config > style（兼容四段式与旧数据） */
export function readConfig(component: VisualComponent | null | undefined, key: string, fallback?: unknown): any {
  if (!component) return fallback;
  if (component.base && (component.base as any)[key] !== undefined) return (component.base as any)[key];
  if (component.special && component.special[key] !== undefined) return component.special[key];
  if (component.config && component.config[key] !== undefined) return component.config[key];
  if (component.style && component.style[key] !== undefined) return component.style[key];
  return fallback;
}

/** 解析 JSON 字段（textarea/json 控件），失败返回 fallback */
export function parseJson<T = unknown>(raw: unknown, fallback: T): T {
  if (raw === undefined || raw === null || raw === '') return fallback;
  if (typeof raw !== 'string') return raw as T;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

// ============================================
// 2026-09-14 修复（FE-21/32/33/35）：图表/表格数据统一归一化
// 背景：解析管线（取值路径/映射/JS 转换）可能产出对象、标量、undefined；
// 原实现直接裸读 .label/.value → ECharts 渲染 "undefined" 字样 / 静默不绘制 / 回落硬编码演示数据。
// ============================================

/** 数据归一化为行数组：
 * - 数组 → 原样（元素为原始值时包装成 { value }）
 * - 对象（非数组）→ 单行 [对象]
 * - 标量（number/string/boolean）→ [{ value: 标量 }]
 * - null/undefined → null（表示"无数据"，由调用方决定演示或空态）
 */
export function normalizeRows(data: unknown): Array<Record<string, any>> | null {
  if (data === undefined || data === null) return null;
  if (Array.isArray(data)) {
    return data.map((d) => (d !== null && typeof d === 'object' ? (d as Record<string, any>) : { value: d }));
  }
  if (typeof data === 'object') return [data as Record<string, any>];
  return [{ value: data }];
}

/** 行标签兜底：label → name → 空字符串（避免 ECharts 类目轴渲染字面量 "undefined"） */
export function rowLabel(r: Record<string, any> | null | undefined): string {
  const v = r?.label ?? r?.name;
  return v === undefined || v === null ? '' : String(v);
}

/** 行数值兜底：Number(value) 非法 → 0（避免 NaN 直入 ECharts 静默不绘制） */
export function rowValue(r: Record<string, any> | null | undefined): number {
  const n = Number(r?.value);
  return Number.isFinite(n) ? n : 0;
}

/** 图例配置（regular 层 legendShow/legendPosition/legendOrient 统一映射，与 chartOptionFactory 保持一致） */
export function buildLegend(config: Record<string, unknown>, fontSize = 11): any | undefined {
  if (config.legendShow === false) return undefined;
  return {
    show: true,
    top: config.legendPosition === 'top' || !config.legendPosition ? 4 : undefined,
    right: config.legendPosition === 'right' ? 4 : undefined,
    bottom: config.legendPosition === 'bottom' ? 4 : undefined,
    left: config.legendPosition === 'left' ? 4 : undefined,
    orient: (config.legendOrient as string) || 'horizontal',
    textStyle: { color: '#ccc', fontSize },
  };
}
